import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  FormControl,
  Select,
  MenuItem,
  CssBaseline,
} from '@mui/material';
import {
  CheckCircle,
  Star,
  Business,
  Payment,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import { fetchSucursales } from '../../../utils/generalsFetch';
import AppTheme from '../../../shared-theme/AppTheme';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, message: '', severity: 'success' });

  // Fetch subscription plans
  const fetchPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/plans`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // Create new subscription for selected branch
  const createSubscription = async (planId) => {
    if (!selectedBranch) {
      setAlertDialog({
        open: true,
        message: 'Por favor selecciona una sucursal',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      console.log('Creating subscription for branch:', selectedBranch);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          idcompany: localStorage.getItem('idcompany'),
          branch_id: selectedBranch.id,
          plan_id: planId,
          created_by: localStorage.getItem('id')
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAlertDialog({
          open: true,
          message: data.message || 'Suscripción procesada exitosamente',
          severity: 'success'
        });
        setShowConfirmDialog(false);
        // Navigate back to subscription management
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        const errorData = await response.json();
        
        // If it's a "branch already has active subscription" error, try the change-plan endpoint
        if (errorData.error && errorData.error.includes('already has an active subscription')) {
          const changeResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/change-plan`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
            },
            body: JSON.stringify({
              idcompany: localStorage.getItem('idcompany'),
              branch_id: selectedBranch.id,
              plan_id: planId,
              created_by: localStorage.getItem('id')
            })
          });

          if (changeResponse.ok) {
            const changeData = await changeResponse.json();
            setAlertDialog({
              open: true,
              message: changeData.message || 'Plan cambiado exitosamente',
              severity: 'success'
            });
            setShowConfirmDialog(false);
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          } else {
            const changeErrorData = await changeResponse.json();
            setAlertDialog({
              open: true,
              message: changeErrorData.error || 'Error al cambiar el plan',
              severity: 'error'
            });
          }
        } else {
          setAlertDialog({
            open: true,
            message: errorData.error || 'Error al crear suscripción',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      setAlertDialog({
        open: true,
        message: 'Error al crear suscripción',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchSucursales({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') }).then((data) => {
      const transformedBranches = data.map(branch => ({
        id: branch.value,
        name: branch.label
      }));
      setBranches(transformedBranches);
      if (transformedBranches.length > 0 && !selectedBranch) {
        setSelectedBranch(transformedBranches[0]);
      }
    });
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount);
  };

  const renderBranchSelector = () => {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom textAlign={"left"}>
            Seleccionar Sucursal
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedBranch?.id || ''}
              onChange={(e) => {
                const branch = branches.find(b => b.id === e.target.value);
                console.log('Branch selected:', branch);
                setSelectedBranch(branch);
              }}
              displayEmpty
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="" disabled>
                Selecciona una sucursal
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
    );
  };

  const renderPlans = () => {
    return (
      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item size={{xs:12, md:3}} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(85, 180, 172, 0.02) 0%, rgba(24, 88, 107, 0.02) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    background: 'linear-gradient(135deg, rgba(85, 180, 172, 0.05) 0%, rgba(24, 88, 107, 0.05) 100%)',
                  }
                }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {plan.name}
                  </Typography>
                  {plan.name === 'Plan Profesional' && (
                    <Star sx={{ color: '#ffd700' }} />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {plan.description}
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 1, color: '#55b4ac' }}>
                  {formatCurrency(plan.price)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    /{plan.billing_cycle === 'monthly' ? 'mes' : 'año'}
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  por sucursal • Usuario adicional: {formatCurrency(plan.additional_user_price || 0)}/mes
                </Typography>
                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!selectedBranch}
                  onClick={() => {
                    setSelectedPlan(plan);
                    setShowConfirmDialog(true);
                  }}
                  sx={{ fontWeight: 'bold' }}
                  color="primarybutton"
                >
                  Seleccionar Plan
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderConfirmDialog = () => {
    if (!selectedPlan) return null;

    return (
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirmar Suscripción
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Al confirmar, la suscripción comenzará inmediatamente para la sucursal seleccionada.
          </Alert>
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Sucursal seleccionada:
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {selectedBranch?.name || 'Ninguna sucursal seleccionada'}
            </Typography>
          </Box>
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {selectedPlan.name}
            </Typography>
            <Typography variant="h4" color="primary.main" fontWeight={700} sx={{ color: '#55b4ac' }}>
              {formatCurrency(selectedPlan.price)}
              <Typography component="span" variant="body2" color="text.secondary">
                /{selectedPlan.billing_cycle === 'monthly' ? 'mes' : 'año'}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedPlan.description}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => createSubscription(selectedPlan.id)}
            disabled={loading}
            sx={{ fontWeight: 'bold' }}
          >
            {loading ? 'Procesando...' : 'Confirmar Suscripción'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Seleccionar Plan de Suscripción
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign={"left"}>
            Elige el plan perfecto para tu sucursal
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ fontWeight: 'bold' }}
        >
          Volver a Suscripciones
        </Button>
      </Box>

      {renderBranchSelector()}
      {renderPlans()}
      {renderConfirmDialog()}

      <AlertMessageDialog
        open={alertDialog.open}
        message={alertDialog.message}
        severity={alertDialog.severity}
        handleClose={() => setAlertDialog({ ...alertDialog, open: false })}
      />
    </Box>
    </AppTheme>
  );
};

export default SubscriptionPlans;
